import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, Trash2, Plus, Bot, User, Award, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api, API_BASE } from "@/lib/api";
import ModuleQuizDialog from "@/components/ModuleQuizDialog";

const MODES = [
  { id: "practice", label: "Language practice" },
  { id: "math", label: "Math help" },
  { id: "general", label: "General" },
];

const LANGUAGES = ["French", "English", "Spanish"];

const MODEL_BADGE = {
  openai: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  anthropic: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  gemini: "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
};

function SessionList({ sessions, sessionId, openSession, deleteSession, className = "" }) {
  return (
    <ScrollArea className={`${className} pr-2`}>
      {sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No sessions yet.</p>
      ) : (
        <div className="space-y-1">
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => openSession(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors group ${
                s.id === sessionId
                  ? "bg-primary/10 text-foreground"
                  : "hover:bg-muted text-muted-foreground"
              }`}
              data-testid="ai-session-item"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate flex-1">{s.title || "Untitled chat"}</span>
                <Trash2
                  className="h-3.5 w-3.5 opacity-60 lg:opacity-0 group-hover:opacity-70 hover:opacity-100 shrink-0"
                  onClick={(e) => deleteSession(s.id, e)}
                />
              </div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-70">
                {s.model} · {s.mode}
              </div>
            </button>
          ))}
        </div>
      )}
    </ScrollArea>
  );
}

export default function AiPractice() {
  const [models, setModels] = useState([]);
  const [provider, setProvider] = useState("openai");
  const [mode, setMode] = useState("practice");
  const [language, setLanguage] = useState("French");

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingReply, setStreamingReply] = useState("");
  const [quizOpen, setQuizOpen] = useState(false);

  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/ai/models").then((r) => setModels(r.data.models));
    loadSessions();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingReply]);

  const loadSessions = async () => {
    const { data } = await api.get("/ai/sessions");
    setSessions(data.sessions);
  };

  const openSession = async (id) => {
    const { data } = await api.get(`/ai/sessions/${id}/messages`);
    setSessionId(id);
    setProvider(data.session.model);
    setMode(data.session.mode || "practice");
    if (data.session.language) setLanguage(data.session.language);
    setMessages(data.messages);
    setStreamingReply("");
  };

  const newSession = () => {
    if (abortRef.current) abortRef.current.abort();
    setSessionId(null);
    setMessages([]);
    setStreamingReply("");
    setInput("");
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/ai/sessions/${id}`);
    if (id === sessionId) newSession();
    loadSessions();
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const token = localStorage.getItem("polymath_token");
    const nextMessages = [...messages, { id: `tmp-${Date.now()}`, role: "user", content: text, model: provider }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    setStreamingReply("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionId || "new",
          model: provider,
          message: text,
          mode,
          language: mode === "practice" ? language : null,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullReply = "";
      let newSessionId = sessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          let event = "message";
          let data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) event = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (event === "session" && parsed.session_id) {
              newSessionId = parsed.session_id;
              setSessionId(parsed.session_id);
            } else if (event === "error") {
              toast.error("AI error", { description: parsed.error });
            } else if (event === "done") {
              // handled below
            } else if (parsed.delta) {
              fullReply += parsed.delta;
              setStreamingReply(fullReply);
            }
          } catch {
            /* ignore */
          }
        }
      }

      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: "assistant", content: fullReply, model: provider },
      ]);
      setStreamingReply("");
      if (newSessionId) loadSessions();
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error("Chat failed", { description: String(err.message || err) });
      }
      setStreamingReply("");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="flex items-center gap-3 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
            AI Practice Buddy
          </h1>
          <p className="text-muted-foreground">
            Practice French, English or Spanish — or get Math help — with your choice of frontier model.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 lg:gap-6">
        {/* Sidebar — collapsed on mobile */}
        <Card className="p-4 h-fit lg:sticky lg:top-24">
          <Button
            onClick={newSession}
            className="w-full rounded-full mb-3"
            data-testid="ai-new-chat-button"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New chat
          </Button>
          <details className="lg:hidden" open={false}>
            <summary className="cursor-pointer text-xs uppercase tracking-wider font-semibold text-muted-foreground py-1 select-none">
              Recent sessions ({sessions.length})
            </summary>
            <SessionList
              sessions={sessions}
              sessionId={sessionId}
              openSession={openSession}
              deleteSession={deleteSession}
              className="h-[180px] mt-2"
            />
          </details>
          <div className="hidden lg:block">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
              Recent sessions
            </p>
            <SessionList
              sessions={sessions}
              sessionId={sessionId}
              openSession={openSession}
              deleteSession={deleteSession}
              className="h-[280px]"
            />
          </div>
        </Card>

        {/* Chat column */}
        <Card className="flex flex-col h-[75vh] min-h-[460px] lg:h-[70vh] lg:min-h-[520px]">
          {/* Controls */}
          <div className="p-3 sm:p-4 border-b border-border flex flex-wrap gap-2 sm:gap-3 items-center">
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="w-full sm:w-[200px] rounded-full" data-testid="ai-model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="flex-1 sm:w-[180px] sm:flex-none rounded-full" data-testid="ai-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODES.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {mode === "practice" && (
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="flex-1 sm:w-[160px] sm:flex-none rounded-full" data-testid="ai-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="w-full sm:w-auto sm:ml-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setQuizOpen(true)}
                disabled={!sessionId || messages.length < 4 || sending}
                className="w-full sm:w-auto rounded-full"
                data-testid="ai-finish-module-button"
              >
                <Award className="h-4 w-4 mr-1.5" /> Finish module
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-messages">
            {messages.length === 0 && !streamingReply && (
              <div className="h-full flex items-center justify-center text-center">
                <div className="max-w-sm">
                  <Bot className="h-10 w-10 mx-auto text-primary/70" />
                  <h3 className="mt-3 font-heading text-lg font-semibold">
                    Start a conversation
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Pick a model, a mode, and send a message to begin.
                  </p>
                </div>
              </div>
            )}

            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}

            {streamingReply && (
              <MessageBubble msg={{ role: "assistant", content: streamingReply, model: provider }} streaming />
            )}
          </div>

          {/* Composer */}
          <form onSubmit={send} className="border-t border-border p-4 flex gap-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                mode === "practice"
                  ? `Say something in ${language}…`
                  : mode === "math"
                  ? "Describe the problem you want help with…"
                  : "Ask anything…"
              }
              rows={2}
              className="resize-none"
              disabled={sending}
              data-testid="ai-chat-input"
            />
            <Button
              type="submit"
              size="lg"
              className="rounded-full px-5 h-auto self-stretch"
              disabled={sending || !input.trim()}
              data-testid="ai-chat-send-button"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </Card>
      </div>

      <ModuleQuizDialog
        open={quizOpen}
        onOpenChange={setQuizOpen}
        sessionId={sessionId}
        onCertificateIssued={() => toast.success("Certificate issued! Check your Certificates page.")}
      />
    </div>
  );
}

function MessageBubble({ msg, streaming = false }) {
  const isUser = msg.role === "user";
  const [playing, setPlaying] = useState(false);

  const play = async () => {
    if (!msg.content) return;
    setPlaying(true);
    const token = localStorage.getItem("polymath_token");
    try {
      const res = await fetch(`${API_BASE}/ai/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: msg.content.slice(0, 1500), voice: "nova" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlaying(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (e) {
      toast.error("Could not play audio");
      setPlaying(false);
    }
  };

  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Bot className="h-4 w-4" />
        </div>
      )}
      <div
        className={`max-w-[85%] sm:max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-muted text-foreground rounded-bl-md"
        }`}
      >
        {msg.content}
        {streaming && <span className="ml-1 inline-block h-2 w-1.5 bg-current animate-pulse align-middle" />}
        {!isUser && !streaming && (
          <div className="mt-2 flex items-center gap-2">
            {msg.model && (
              <Badge
                variant="outline"
                className={`text-[10px] uppercase tracking-wider font-medium border-transparent ${
                  MODEL_BADGE[msg.model] || ""
                }`}
              >
                {msg.model}
              </Badge>
            )}
            <button
              onClick={play}
              disabled={playing}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
              data-testid="ai-tts-button"
            >
              {playing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Volume2 className="h-3 w-3" />
              )}
              Listen
            </button>
          </div>
        )}
      </div>
      {isUser && (
        <div className="h-8 w-8 shrink-0 rounded-full bg-foreground text-background flex items-center justify-center">
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}
